import React, { useState, useEffect } from 'react';
import gisService from '@/modules/utility/gis/services/gisService';
import { PageSkeleton } from '@/components/base/Skeleton';
// External Applications Management - CRUD interface for managing external GIS data providers and consumers

interface ExternalApplication {
  id: number;
  app_code: string;
  app_name: string;
  department_name: string;
  app_type: string;
  contact_person: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  application_url: string | null;
  description: string | null;
  auth_mechanism: string;
  api_base_url: string | null;
  auth_config: any;
  app_variables: any;
  is_active: boolean;
  display_order: number;
  created_at?: string;
  updated_at?: string;
}

const ExternalApplications: React.FC = () => {
  // State
  const [applications, setApplications] = useState<ExternalApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<keyof ExternalApplication>('display_order');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingApp, setEditingApp] = useState<ExternalApplication | null>(null);
  const [deletingApp, setDeletingApp] = useState<ExternalApplication | null>(null);
  const [testingConnection, setTestingConnection] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    app_code: '',
    app_name: '',
    department_name: '',
    app_type: 'consumer',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    application_url: '',
    description: '',
    auth_mechanism: 'none',
    api_base_url: '',
    auth_config: '',
    app_variables: '',
    is_active: true,
    display_order: 0
  });

  // Application Variables state (key-value pairs)
  const [appVariables, setAppVariables] = useState<{ key: string; value: string }[]>([]);

  // Authentication configuration states
  const [authMethods, setAuthMethods] = useState<string[]>([]);
  const [authHeaders, setAuthHeaders] = useState<{ key: string; value: string }[]>([]);
  const [authParameters, setAuthParameters] = useState<{ key: string; value: string }[]>([]);
  const [preTokenConfig, setPreTokenConfig] = useState({
    token_url: '',
    refresh_url: '',
    token_headers: [] as { key: string; value: string }[],
    token_parameters: [] as { key: string; value: string }[]
  });

  const itemsPerPage = 10;

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await gisService.getAllExternalApplications();

      if (response.status === 200 && response.data) {
        setApplications(response.data);
      }
    } catch (error) {
      console.error('Error fetching external applications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Type counts
  const providerCount = applications.filter(a => a.app_type === 'provider').length;
  const consumerCount = applications.filter(a => a.app_type === 'consumer').length;
  const bothCount = applications.filter(a => a.app_type === 'both').length;

  // Filter and sort
  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.app_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.app_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.department_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || app.app_type === typeFilter;

    return matchesSearch && matchesType;
  });

  const sortedApplications = [...filteredApplications].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortOrder === 'asc'
      ? (aValue > bValue ? 1 : -1)
      : (aValue < bValue ? 1 : -1);
  });

  // Pagination
  const totalPages = Math.ceil(sortedApplications.length / itemsPerPage);
  const paginatedApplications = sortedApplications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (column: keyof ExternalApplication) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleCreate = () => {
    setEditingApp(null);
    setFormData({
      app_code: '',
      app_name: '',
      department_name: '',
      app_type: 'consumer',
      contact_person: '',
      contact_email: '',
      contact_phone: '',
      application_url: '',
      description: '',
      auth_mechanism: 'none',
      api_base_url: '',
      auth_config: '',
      app_variables: '',
      is_active: true,
      display_order: 0
    });
    setAppVariables([]);
    setAuthMethods([]);
    setAuthHeaders([]);
    setAuthParameters([]);
    setPreTokenConfig({
      token_url: '',
      refresh_url: '',
      token_headers: [],
      token_parameters: []
    });
    setIsFullscreen(false);
    setShowCreateModal(true);
  };

  const handleEdit = (app: ExternalApplication) => {
    setEditingApp(app);
    setFormData({
      app_code: app.app_code,
      app_name: app.app_name,
      department_name: app.department_name,
      app_type: app.app_type,
      contact_person: app.contact_person || '',
      contact_email: app.contact_email || '',
      contact_phone: app.contact_phone || '',
      application_url: app.application_url || '',
      description: app.description || '',
      auth_mechanism: app.auth_mechanism,
      api_base_url: app.api_base_url || '',
      auth_config: app.auth_config ? JSON.stringify(app.auth_config, null, 2) : '',
      app_variables: app.app_variables ? JSON.stringify(app.app_variables, null, 2) : '',
      is_active: app.is_active,
      display_order: app.display_order
    });

    // Parse app_variables JSON into key-value pairs
    if (app.app_variables && typeof app.app_variables === 'object') {
      const pairs = Object.entries(app.app_variables).map(([key, value]) => ({
        key,
        value: String(value)
      }));
      setAppVariables(pairs);
    } else {
      setAppVariables([]);
    }

    // Parse auth_config JSON into authentication states
    const methods: string[] = [];
    if (app.auth_config && typeof app.auth_config === 'object') {
      const config = app.auth_config;

      // Check for headers
      if (config.headers && Array.isArray(config.headers)) {
        methods.push('header');
        setAuthHeaders(config.headers.map((h: any) => ({
          key: h.key || h.name || '',
          value: h.value || ''
        })));
      } else {
        setAuthHeaders([]);
      }

      // Check for parameters
      if (config.parameters && Array.isArray(config.parameters)) {
        methods.push('parameters');
        setAuthParameters(config.parameters.map((p: any) => ({
          key: p.key || p.name || '',
          value: p.value || ''
        })));
      } else {
        setAuthParameters([]);
      }

      // Check for pre-token
      if (config.pre_token) {
        methods.push('token');
        setPreTokenConfig({
          token_url: config.pre_token.token_url || '',
          refresh_url: config.pre_token.refresh_url || '',
          token_headers: Array.isArray(config.pre_token.headers)
            ? config.pre_token.headers.map((h: any) => ({ key: h.key || h.name || '', value: h.value || '' }))
            : [],
          token_parameters: Array.isArray(config.pre_token.parameters)
            ? config.pre_token.parameters.map((p: any) => ({ key: p.key || p.name || '', value: p.value || '' }))
            : []
        });
      } else {
        setPreTokenConfig({
          token_url: '',
          refresh_url: '',
          token_headers: [],
          token_parameters: []
        });
      }

      setAuthMethods(methods);
    } else {
      setAuthMethods([]);
      setAuthHeaders([]);
      setAuthParameters([]);
      setPreTokenConfig({
        token_url: '',
        refresh_url: '',
        token_headers: [],
        token_parameters: []
      });
    }

    setIsFullscreen(false);
    setShowCreateModal(true);
  };

  const handleDelete = (app: ExternalApplication) => {
    setDeletingApp(app);
    setShowDeleteModal(true);
  };

  const handleTestConnection = async (app: ExternalApplication) => {
    setTestingConnection(app.id);
    try {
      const response = await gisService.testExternalApplicationConnection(app.id);
      alert(`Connection test result:\n${response.data.message}`);
    } catch (error) {
      console.error('Error testing connection:', error);
      alert('Failed to test connection');
    } finally {
      setTestingConnection(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Build auth_config from selected authentication methods
      let authConfig: any = null;

      if (authMethods.length > 0) {
        authConfig = {};

        // Add headers if selected
        if (authMethods.includes('header') && authHeaders.length > 0) {
          authConfig.headers = authHeaders
            .filter(h => h.key.trim())
            .map(h => ({ key: h.key.trim(), value: h.value }));
        }

        // Add parameters if selected
        if (authMethods.includes('parameters') && authParameters.length > 0) {
          authConfig.parameters = authParameters
            .filter(p => p.key.trim())
            .map(p => ({ key: p.key.trim(), value: p.value }));
        }

        // Add pre-token if selected
        if (authMethods.includes('token')) {
          authConfig.pre_token = {
            token_url: preTokenConfig.token_url,
            refresh_url: preTokenConfig.refresh_url,
            headers: preTokenConfig.token_headers
              .filter(h => h.key.trim())
              .map(h => ({ key: h.key.trim(), value: h.value })),
            parameters: preTokenConfig.token_parameters
              .filter(p => p.key.trim())
              .map(p => ({ key: p.key.trim(), value: p.value }))
          };
        }
      }

      // Convert appVariables key-value pairs to object
      let appVariablesObj = null;
      if (appVariables.length > 0) {
        appVariablesObj = {};
        for (const pair of appVariables) {
          if (pair.key.trim()) {
            appVariablesObj[pair.key.trim()] = pair.value;
          }
        }
      }

      const submitData = {
        ...formData,
        auth_mechanism: authMethods.length === 0 ? 'none' : authMethods.join(','),
        auth_config: authConfig,
        app_variables: appVariablesObj
      };

      if (editingApp) {
        await gisService.updateExternalApplication(editingApp.id, submitData);
      } else {
        await gisService.createExternalApplication(submitData);
      }

      setShowCreateModal(false);
      await fetchData();
    } catch (error) {
      console.error('Error saving external application:', error);
      alert('Failed to save external application');
    }
  };

  const confirmDelete = async () => {
    if (!deletingApp) return;

    try {
      await gisService.deleteExternalApplication(deletingApp.id);
      setShowDeleteModal(false);
      setDeletingApp(null);
      await fetchData();
    } catch (error) {
      console.error('Error deleting external application:', error);
      alert('Failed to delete external application');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: value === '' ? 0 : Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Application Variables management functions
  const handleAddVariable = () => {
    setAppVariables([...appVariables, { key: '', value: '' }]);
  };

  const handleRemoveVariable = (index: number) => {
    setAppVariables(appVariables.filter((_, i) => i !== index));
  };

  const handleVariableChange = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...appVariables];
    updated[index][field] = value;
    setAppVariables(updated);
  };

  // Authentication method toggle
  const handleAuthMethodToggle = (method: string) => {
    if (authMethods.includes(method)) {
      setAuthMethods(authMethods.filter(m => m !== method));
    } else {
      setAuthMethods([...authMethods, method]);
    }
  };

  // Header management
  const handleAddHeader = () => {
    setAuthHeaders([...authHeaders, { key: '', value: '' }]);
  };

  const handleRemoveHeader = (index: number) => {
    setAuthHeaders(authHeaders.filter((_, i) => i !== index));
  };

  const handleHeaderChange = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...authHeaders];
    updated[index][field] = value;
    setAuthHeaders(updated);
  };

  // Parameter management
  const handleAddParameter = () => {
    setAuthParameters([...authParameters, { key: '', value: '' }]);
  };

  const handleRemoveParameter = (index: number) => {
    setAuthParameters(authParameters.filter((_, i) => i !== index));
  };

  const handleParameterChange = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...authParameters];
    updated[index][field] = value;
    setAuthParameters(updated);
  };

  // Pre-token header management
  const handleAddTokenHeader = () => {
    setPreTokenConfig({
      ...preTokenConfig,
      token_headers: [...preTokenConfig.token_headers, { key: '', value: '' }]
    });
  };

  const handleRemoveTokenHeader = (index: number) => {
    setPreTokenConfig({
      ...preTokenConfig,
      token_headers: preTokenConfig.token_headers.filter((_, i) => i !== index)
    });
  };

  const handleTokenHeaderChange = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...preTokenConfig.token_headers];
    updated[index][field] = value;
    setPreTokenConfig({ ...preTokenConfig, token_headers: updated });
  };

  // Pre-token parameter management
  const handleAddTokenParameter = () => {
    setPreTokenConfig({
      ...preTokenConfig,
      token_parameters: [...preTokenConfig.token_parameters, { key: '', value: '' }]
    });
  };

  const handleRemoveTokenParameter = (index: number) => {
    setPreTokenConfig({
      ...preTokenConfig,
      token_parameters: preTokenConfig.token_parameters.filter((_, i) => i !== index)
    });
  };

  const handleTokenParameterChange = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...preTokenConfig.token_parameters];
    updated[index][field] = value;
    setPreTokenConfig({ ...preTokenConfig, token_parameters: updated });
  };

  if (isLoading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">External Applications</h1>
        <p className="mt-2 text-gray-600">
          Manage external applications that provide or consume GIS data
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Applications</p>
              <p className="text-2xl font-bold text-gray-900">{applications.length}</p>
            </div>
            <i className="ri-apps-line text-3xl text-blue-600"></i>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Providers</p>
              <p className="text-2xl font-bold text-gray-900">{providerCount}</p>
            </div>
            <i className="ri-upload-cloud-line text-3xl text-green-600"></i>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Consumers</p>
              <p className="text-2xl font-bold text-gray-900">{consumerCount}</p>
            </div>
            <i className="ri-download-cloud-line text-3xl text-purple-600"></i>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Both</p>
              <p className="text-2xl font-bold text-gray-900">{bothCount}</p>
            </div>
            <i className="ri-cloud-line text-3xl text-orange-600"></i>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, code, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Type Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setTypeFilter('all')}
              className={`px-4 py-2 rounded-lg ${
                typeFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({applications.length})
            </button>
            <button
              onClick={() => setTypeFilter('provider')}
              className={`px-4 py-2 rounded-lg ${
                typeFilter === 'provider'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Provider ({providerCount})
            </button>
            <button
              onClick={() => setTypeFilter('consumer')}
              className={`px-4 py-2 rounded-lg ${
                typeFilter === 'consumer'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Consumer ({consumerCount})
            </button>
            <button
              onClick={() => setTypeFilter('both')}
              className={`px-4 py-2 rounded-lg ${
                typeFilter === 'both'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Both ({bothCount})
            </button>
          </div>

          {/* Create Button */}
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <i className="ri-add-line"></i>
            Create Application
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort('app_code')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  App Code {sortBy === 'app_code' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('app_name')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Application Name {sortBy === 'app_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('department_name')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Department {sortBy === 'department_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('app_type')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Type {sortBy === 'app_type' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Auth
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedApplications.map((app) => (
                <tr key={app.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{app.app_code}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{app.app_name}</div>
                    {app.contact_person && (
                      <div className="text-sm text-gray-500">{app.contact_person}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {app.department_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      app.app_type === 'provider'
                        ? 'bg-green-100 text-green-800'
                        : app.app_type === 'consumer'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {app.app_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {app.auth_mechanism}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      app.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {app.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleTestConnection(app)}
                      disabled={testingConnection === app.id}
                      className="text-green-600 hover:text-green-900 mr-3 disabled:opacity-50"
                      title="Test Connection"
                    >
                      <i className={`${testingConnection === app.id ? 'ri-loader-4-line animate-spin' : 'ri-plug-line'}`}></i>
                    </button>
                    <button
                      onClick={() => handleEdit(app)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <i className="ri-edit-line"></i> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(app)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <i className="ri-delete-bin-line"></i> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, sortedApplications.length)}
                  </span>{' '}
                  of <span className="font-medium">{sortedApplications.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${
          isFullscreen ? 'p-0 z-[9999]' : 'p-4 z-50'
        }`}>
          <div className={`bg-white shadow-xl overflow-y-auto transition-all ${
            isFullscreen
              ? 'w-full h-full max-w-full max-h-full m-0 rounded-none'
              : 'max-w-4xl w-full max-h-[90vh] rounded-lg'
          }`}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingApp ? 'Edit External Application' : 'Create External Application'}
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    className="text-gray-400 hover:text-gray-600"
                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                  >
                    <i className={`text-2xl ${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}`}></i>
                  </button>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6 bg-gray-50">
              {/* Basic Information */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-5 py-3">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center">
                    <i className="ri-information-line mr-2 text-blue-600"></i>
                    Basic Information
                  </h3>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Application Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="app_code"
                      value={formData.app_code}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Application Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="app_name"
                      value={formData.app_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="department_name"
                      value={formData.department_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Application Type
                    </label>
                    <select
                      name="app_type"
                      value={formData.app_type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="provider">Provider</option>
                      <option value="consumer">Consumer</option>
                      <option value="both">Both</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Application URL
                    </label>
                    <input
                      type="url"
                      name="application_url"
                      value={formData.application_url}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200 px-5 py-3">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center">
                    <i className="ri-contacts-line mr-2 text-green-600"></i>
                    Contact Information
                  </h3>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Person
                    </label>
                    <input
                      type="text"
                      name="contact_person"
                      value={formData.contact_person}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  </div>
                </div>
              </div>

              {/* API Configuration */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-200 px-5 py-3">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center">
                    <i className="ri-code-box-line mr-2 text-purple-600"></i>
                    API Configuration
                  </h3>
                </div>
                <div className="p-5">
                  <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Base URL
                    </label>
                    <input
                      type="url"
                      name="api_base_url"
                      value={formData.api_base_url}
                      onChange={handleInputChange}
                      placeholder="https://api.example.com/v1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Authentication Methods */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Authentication Methods
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={authMethods.includes('header')}
                          onChange={() => handleAuthMethodToggle('header')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Header Authentication</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={authMethods.includes('parameters')}
                          onChange={() => handleAuthMethodToggle('parameters')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">URL Parameters</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={authMethods.includes('token')}
                          onChange={() => handleAuthMethodToggle('token')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Pre Token Authentication</span>
                      </label>
                    </div>
                    {authMethods.length === 0 && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-700">
                          <i className="ri-information-line mr-1"></i>
                          No authentication selected. Access can be controlled by firewall configuration and IP whitelisting.
                        </p>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              </div>

              {/* Header Authentication Config - Full Width */}
              {authMethods.includes('header') && (
                <div className="mb-6">
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-base font-semibold text-gray-900">Header Authentication</h4>
                      <button
                        type="button"
                        onClick={handleAddHeader}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center gap-1"
                      >
                        <i className="ri-add-line"></i>
                        Add Header
                      </button>
                    </div>
                    <div className="space-y-2">
                      {authHeaders.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">No headers defined. Click "Add Header" to create one.</p>
                      ) : (
                        authHeaders.map((header, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="text"
                              value={header.key}
                              onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                              placeholder="Header name (e.g., Authorization)"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            <input
                              type="text"
                              value={header.value}
                              onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                              placeholder="Header value"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveHeader(index)}
                              className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* URL Parameters Authentication Config - Full Width */}
              {authMethods.includes('parameters') && (
                <div className="mb-6">
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-base font-semibold text-gray-900">URL Parameters Authentication</h4>
                      <button
                        type="button"
                        onClick={handleAddParameter}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center gap-1"
                      >
                        <i className="ri-add-line"></i>
                        Add Parameter
                      </button>
                    </div>
                    <div className="space-y-2">
                      {authParameters.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">No parameters defined. Click "Add Parameter" to create one.</p>
                      ) : (
                        authParameters.map((param, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="text"
                              value={param.key}
                              onChange={(e) => handleParameterChange(index, 'key', e.target.value)}
                              placeholder="Parameter name (e.g., api_key)"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            <input
                              type="text"
                              value={param.value}
                              onChange={(e) => handleParameterChange(index, 'value', e.target.value)}
                              placeholder="Parameter value"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveParameter(index)}
                              className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Pre-Token Authentication Config - Full Width */}
              {authMethods.includes('token') && (
                <div className="mb-6">
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <h4 className="text-base font-semibold text-gray-900 mb-4">Pre-Token Authentication</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Token URL <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="url"
                          value={preTokenConfig.token_url}
                          onChange={(e) => setPreTokenConfig({ ...preTokenConfig, token_url: e.target.value })}
                          placeholder="https://api.example.com/auth/token"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Token Refresh URL
                        </label>
                        <input
                          type="url"
                          value={preTokenConfig.refresh_url}
                          onChange={(e) => setPreTokenConfig({ ...preTokenConfig, refresh_url: e.target.value })}
                          placeholder="https://api.example.com/auth/refresh"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>

                      {/* Token Headers */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">Token Request Headers</label>
                          <button
                            type="button"
                            onClick={handleAddTokenHeader}
                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 flex items-center gap-1"
                          >
                            <i className="ri-add-line"></i> Add
                          </button>
                        </div>
                        <div className="space-y-2">
                          {preTokenConfig.token_headers.length === 0 ? (
                            <p className="text-sm text-gray-500 italic">No headers defined.</p>
                          ) : (
                            preTokenConfig.token_headers.map((header, index) => (
                              <div key={index} className="flex gap-2">
                                <input
                                  type="text"
                                  value={header.key}
                                  onChange={(e) => handleTokenHeaderChange(index, 'key', e.target.value)}
                                  placeholder="Header name"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                />
                                <input
                                  type="text"
                                  value={header.value}
                                  onChange={(e) => handleTokenHeaderChange(index, 'value', e.target.value)}
                                  placeholder="Header value"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTokenHeader(index)}
                                  className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200"
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Token Parameters */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">Token Request Parameters</label>
                          <button
                            type="button"
                            onClick={handleAddTokenParameter}
                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 flex items-center gap-1"
                          >
                            <i className="ri-add-line"></i> Add
                          </button>
                        </div>
                        <div className="space-y-2">
                          {preTokenConfig.token_parameters.length === 0 ? (
                            <p className="text-sm text-gray-500 italic">No parameters defined.</p>
                          ) : (
                            preTokenConfig.token_parameters.map((param, index) => (
                              <div key={index} className="flex gap-2">
                                <input
                                  type="text"
                                  value={param.key}
                                  onChange={(e) => handleTokenParameterChange(index, 'key', e.target.value)}
                                  placeholder="Parameter name"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                />
                                <input
                                  type="text"
                                  value={param.value}
                                  onChange={(e) => handleTokenParameterChange(index, 'value', e.target.value)}
                                  placeholder="Parameter value"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTokenParameter(index)}
                                  className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200"
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Application Variables */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-gray-200 px-5 py-3 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center">
                    <i className="ri-settings-3-line mr-2 text-amber-600"></i>
                    Application Variables
                  </h3>
                  <button
                    type="button"
                    onClick={handleAddVariable}
                    className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 flex items-center gap-1 shadow-sm"
                  >
                    <i className="ri-add-line"></i>
                    Add Variable
                  </button>
                </div>
                <div className="p-5">
                  <div className="space-y-2">
                  {appVariables.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No variables defined. Click "Add Variable" to create one.</p>
                  ) : (
                    appVariables.map((variable, index) => (
                      <div key={index} className="flex gap-2 items-start">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={variable.key}
                            onChange={(e) => handleVariableChange(index, 'key', e.target.value)}
                            placeholder="Variable name (e.g., timeout)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={variable.value}
                            onChange={(e) => handleVariableChange(index, 'value', e.target.value)}
                            placeholder="Value (e.g., 30)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveVariable(index)}
                          className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200"
                          title="Remove variable"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    ))
                  )}
                  </div>
                  <p className="mt-3 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                    <i className="ri-information-line mr-1"></i>
                    Define key-value pairs for application configuration
                  </p>
                </div>
              </div>

              {/* Additional Settings */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 px-5 py-3">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center">
                    <i className="ri-tools-line mr-2 text-slate-600"></i>
                    Additional Settings
                  </h3>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Order
                    </label>
                    <input
                      type="number"
                      name="display_order"
                      value={formData.display_order}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Active
                    </label>
                  </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6 bg-white px-6 py-4 rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-2.5 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 transition-colors shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
                >
                  {editingApp ? 'Update Application' : 'Create Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Delete External Application
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to delete <strong>{deletingApp.app_name}</strong>?
              This action cannot be undone.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalApplications;
